import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import CurationFilters from '@components/CurationFilters';
import { CurationStatusBadge } from '@components/CurationStatusBadge';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import DiscernAssignedUserDropdown from '@components/DiscernAssignedUserDropdown';

import { DISCERN_JOBS } from '@constants/routes';

import { useDeleteDiscernDocument } from '@hooks/api/discern';
import { useCuration } from '@hooks/useCuration';
import { useDiscernFilters } from '@hooks/useDiscernFilters';
import { useRetryAutoCuration } from '@hooks/useRetryAutoCuration';

import {
  applyClientFilters,
  buildAssignedUsersMap,
  type DiscernRowData,
  transformDiscernDocuments,
} from '@utils/discern-transforms';
import { formatDate } from '@utils/format';

import { useListDiscernDocumentsQuery } from 'generated/discern.generated';
import type { CurationStatus } from 'generated/types';
import { RoleName } from 'generated/types';

import DeleteDiscernButton from './Component/DiscernDeleteButton';
import RetryAutoCurationButton from './Component/RetryAutoCurationButton';

const messages = defineMessages({
  title: {
    id: 'discernSummary.title',
    defaultMessage: 'Overall Summary',
  },
  noResources: {
    id: 'discernSummary.noResources',
    defaultMessage: 'No resources',
  },
});

export type { DiscernRowData as RowData };

const DiscernSummary: React.FC<{
  title?: React.ReactNode;
}> = ({ title }) => {
  const { formatMessage } = useIntl();
  const { palette } = useTheme();
  const { viewAsPatient } = useCuration();
  const { handleRetryDocument, retryingDocumentId } = useRetryAutoCuration();

  const {
    serverFilters,
    clientFilters,
    setServerFilter,
    setClientFilter,
    clearAllFilters,
    toQueryInput,
    hasActiveFilters,
  } = useDiscernFilters();

  const { data: discernData, loading } = useListDiscernDocumentsQuery({
    variables: { input: toQueryInput() },
  });
  const [deleteDiscernDocument] = useDeleteDiscernDocument();

  const handleDeleteDocument = async (id: string) => {
    await deleteDiscernDocument({ discern_document_id: id });
  };

  const docs = React.useMemo(
    () => discernData?.listDiscernDocuments ?? [],
    [discernData?.listDiscernDocuments]
  );
  const assignedUsersMap = React.useMemo(
    () => buildAssignedUsersMap(docs),
    [docs]
  );
  const allRows = React.useMemo(() => transformDiscernDocuments(docs), [docs]);
  const tableData = React.useMemo(
    () => applyClientFilters(allRows, clientFilters),
    [allRows, clientFilters]
  );

  const availableUploaders = React.useMemo(
    () => [...new Set(allRows.map((r) => r.uploadedBy).filter(Boolean))].sort(),
    [allRows]
  );

  const handlePatientClick = async ({ vitallPatient }: DiscernRowData) => {
    if (!vitallPatient?.user_id) {
      return;
    }
    await viewAsPatient(vitallPatient.user_id);
    navigate(DISCERN_JOBS);
  };

  return (
    <ContentLoader loading={loading}>
      <CurationFilters
        serverFilters={serverFilters}
        clientFilters={clientFilters}
        onServerFilterChange={setServerFilter}
        onClientFilterChange={setClientFilter}
        onClearAll={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
        availableUploaders={availableUploaders}
        showPatientSearch
      />
      <MuiDataTable
        columns={[
          {
            name: 'patientName',
            className: 'small',
            label: 'Patient Name',
            options: {
              customBodyRenderLite: (dataIndex) => {
                const rowData = tableData[dataIndex];
                return (
                  <Box
                    component="span"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePatientClick(rowData);
                    }}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'inline-block',
                      '&:hover': {
                        color: palette.primary.main,
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    {rowData.patientName}
                  </Box>
                );
              },
              filter: false,
              sort: true,
              searchable: true,
            },
          },
          {
            name: 'filename',
            className: 'small',
            label: 'Filename',
            options: { filter: true, sort: true, searchable: true },
          },
          {
            name: 'uploadedBy',
            className: 'small',
            label: 'Uploaded By',
            options: { filter: false, sort: true, searchable: true },
          },
          {
            name: 'dateUploaded',
            className: 'date',
            label: 'Date Uploaded',
            options: {
              customBodyRender: (date: string) =>
                formatDate({ date, formatter: 'datetime' }),
              filter: false,
              sort: true,
              searchable: true,
            },
          },
          {
            name: 'lastUpdated',
            className: 'date',
            label: 'Last Updated',
            options: {
              customBodyRender: (date: string) =>
                formatDate({ date, formatter: 'datetime' }),
              filter: true,
              sort: true,
              searchable: true,
            },
          },
          {
            name: 'status',
            label: 'Status',
            className: 'small',
            options: {
              customBodyRender: (status: CurationStatus) => {
                return <CurationStatusBadge status={status} />;
              },
              filter: false,
              sort: true,
            },
          },
          {
            name: 'assignedUser',
            className: 'small',
            label: 'Assigned User',
            options: {
              customBodyRenderLite: (dataIndex) => {
                const rowData = tableData[dataIndex];
                return (
                  <DiscernAssignedUserDropdown
                    name={rowData.assignedUserName}
                    value={rowData.assignedUserId}
                    discernDocumentId={rowData.discernDocumentId}
                    groupingStateJson={rowData.groupingStateJson}
                    role={RoleName.Curator}
                  />
                );
              },
              filter: true,
              sort: true,
              searchable: true,
              filterOptions: {
                renderValue: (value) => {
                  const userName =
                    value in assignedUsersMap ? assignedUsersMap[value] : null;
                  if (!userName) {
                    return '(None)';
                  }
                  return userName;
                },
              },
            },
          },
          {
            name: '',
            options: {
              customHeadLabelRender: () => '',
              customBodyRenderLite: (dataIndex) => {
                const rowData = tableData[dataIndex];
                return (
                  <Box component="span" sx={{ display: 'flex', gap: 0.5 }}>
                    <RetryAutoCurationButton
                      rowData={rowData}
                      handleRetryDocument={handleRetryDocument}
                      retrying={
                        retryingDocumentId === rowData.discernDocumentId
                      }
                    />
                    <DeleteDiscernButton
                      rowData={rowData}
                      handleDeleteDocument={handleDeleteDocument}
                    />
                  </Box>
                );
              },
            },
          },
        ]}
        data={tableData}
        pageParam="discern"
        options={{
          sortOrder: { name: 'dateUploaded', direction: 'desc' },
          sort: true,
          search: true,
          filter: true,
          onRowClick: (_rowData, { dataIndex }) => {
            handlePatientClick(tableData[dataIndex]);
          },
          textLabels: {
            body: { noMatch: formatMessage(messages.noResources) },
          },
        }}
        title={title ?? formatMessage(messages.title)}
      />
    </ContentLoader>
  );
};

export default DiscernSummary;
